import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { SocialEmail, SocialShare, ContentLink, ContentDownload, NoticeLoading, SocialChat } from '@fs/zion-icon'
import { copyToClipboard, isUsingBrowser, isIosDevice, sanitizeProps } from '@fs/zion-frontend-friends'
import { useCanAddPersonalData, usePermission } from '@fs/zion-permissions'
import {
  BillboardButton,
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Ellipsis,
  IconButton,
  LayoutBand,
  ListItem,
  MenuOverlay,
  Paragraph,
  Row,
  Separator,
  Skeleton,
  TextField,
  useMenuOverlay,
  useOverlay,
  useStatusOverlay,
} from '@fs/zion-ui'
import { css } from '@linaria/core'
import {
  LogoFacebook,
  LogoTwitter,
  LogoWhatsApp,
  LogoLine,
  LogoFbMessenger,
  LogoKakaoStory,
  LogoKakaoTalk,
} from '../assets'
import handleAnalytics from './handleAnalytics'

const QRCodeSVG = React.lazy(() => import('qrcode.react').then((module) => ({ default: module.QRCodeSVG })))

const qrCodeCss = css`
  max-width: 300px;
  width: 100%;
  height: 300px;
`

const hasWindow = typeof window !== 'undefined'

// This id was created by the grand council. Do not change it under penalty of shame!
export const facebookAppId = 1261365907621824

// JavaScript SDK key for the FamilySearch Kakao applications found here: https://developers.kakao.com/console/app/610684
const kakaoSDKId = 'f5fef10f1ffcf7e8a06e57845af868ef'

function addAnalyticsParam(url, product, appName) {
  if (!url) return ''
  let urlObj = ''
  try {
    urlObj = new URL(url)
  } catch {
    return urlObj
  }
  urlObj.searchParams.append('cid', `${product}_${appName.toLowerCase()}`)

  return urlObj.toString()
}

const centerWindowPosition = (width, height) => {
  // TODO: SSR needs to be addressed here
  const screenLeft = window.screenLeft ? window.screenLeft : window.screenX
  const screenTop = window.screenTop ? window.screenTop : window.screenY
  return {
    left: screenLeft + window.innerWidth / 2 - width / 2,
    top: screenTop + window.innerHeight / 2 - height / 2,
  }
}

let previouslySharedUrl
const buildWindowSpecs = (config) => {
  // TODO: SSR - No oping for now. this code doesn't make sense for SSR right now. Figure this out
  if (!hasWindow) return ''

  const { width, height } = config.window
  const { left, top } = centerWindowPosition(width, height)
  const windowFeatures = {
    left,
    top,
    width,
    height,
    scrollbars: 'yes', // a11y recommends this be on
    resizable: 'yes', // a11y recommends this be on
    toolbar: 'no',
    location: 'no',
    status: 'no',
    menubar: 'no',
    titlebar: 'no',
  }
  const specs = Object.keys(windowFeatures)
    .map((feature) => `${feature}=${windowFeatures[feature]}`)
    .join(',')
  return specs
}

const openWindow = (config, specs) => {
  const { name, formattedUrl } = config
  if (!formattedUrl) return
  // NOTE: This stuff is following the best practices from Mozilla for window.open
  if (config.window.ref == null || config.window.ref.closed) {
    config.window.ref = window.open(formattedUrl, name, specs)
  } else if (previouslySharedUrl !== formattedUrl) {
    config.window.ref = window.open(formattedUrl, name, specs)
    config.window.ref.focus()
  } else {
    config.window.ref.focus()
  }
  previouslySharedUrl = config.formattedUrl
}

// Keeping this logic inside SocialShare to avoid having a direct dependency on zion-chat
const buildChatUrl = (sharable) => {
  const { url, urlDisplayText, subject } = sharable
  const chatUrl = new URL(window.location.href)
  chatUrl.searchParams.set('chatRoute', '/chat-create/fs_direct')
  if (url) {
    chatUrl.searchParams.set('aboutLink', url)
  }
  if (urlDisplayText) {
    chatUrl.searchParams.set('aboutText', urlDisplayText)
  }
  if (subject) {
    chatUrl.searchParams.set('messageText', subject)
  }
  return chatUrl.toString()
}

const defaultPlatforms = [
  'familysearchChat',
  'facebook',
  'messenger',
  'twitter',
  'whatsApp',
  'line',
  'email',
  'copyLink',
]
const encodedNewLine = '%0A'

const configs = (sharable, product, analytics, downloadUrl) => {
  const { subject, message, url: urlToShare, urlDisplayText, imageUrl } = sharable
  const encodedSubject = encodeURIComponent(subject)
  const encodedMessage = message ? `${encodeURIComponent(message)}${encodedNewLine}` : ''
  const metaDescription = document.head.querySelector('[property="og:description"]')?.content
  const metaImage = document.head.querySelector('[property="og:image"]')?.content

  return {
    familysearchChat: {
      name: 'FamilySearch Chat',
      window: { width: 500, height: 500 },
      url: buildChatUrl(sharable),
      urlToShare,
      urlDisplayText,
      product,
      analytics,
    },
    facebook: {
      name: 'Facebook',
      window: { width: 555, height: 784 },
      url: `https://www.facebook.com/dialog/share?app_id=${facebookAppId}&display=popup&quote=${encodedSubject}&hashtag=FamilySearch&href=`,
      urlToShare,
      product,
      analytics,
    },
    messenger: {
      name: 'Messenger',
      window: { width: 980, height: 645 },
      url: isUsingBrowser('mobile')
        ? `fb-messenger://share/?app_id=${facebookAppId}&link=`
        : `http://www.facebook.com/dialog/send?app_id=${facebookAppId}&redirect_uri=${encodeURIComponent(
            window.location.href
          )}&link=`,
      urlToShare,
      product,
      analytics,
    },
    twitter: {
      name: 'Twitter',
      window: { width: 720, height: 645 },
      url: `https://twitter.com/intent/tweet?text=${encodedSubject}&hashtags=FamilySearch&url=`,
      urlToShare,
      product,
      analytics,
    },
    whatsApp: {
      name: 'WhatsApp',
      window: { width: 500, height: 500 },
      url: `https://api.whatsapp.com/send?text=${encodedSubject}%20`,
      urlToShare,
      product,
      analytics,
    },
    line: {
      name: 'Line',
      window: { width: 500, height: 500 },
      url: `https://social-plugins.line.me/lineit/share?text=${encodedSubject}&url=`,
      urlToShare,
      product,
      analytics,
    },
    kakaoTalk: {
      name: 'Kakao Talk',
      title: subject,
      description: message || metaDescription,
      urlToShare,
      imageUrl: imageUrl || metaImage,
      product,
      analytics,
    },
    kakaoStory: {
      name: 'Kakao Story',
      window: { width: 500, height: 500 },
      url: `https://story.kakao.com/s/share?text=${encodedSubject}&url=`,
      urlToShare,
      product,
      analytics,
    },
    email: {
      name: 'Email',
      window: { width: 720, height: 645 },
      url: `mailto:?subject=${encodedSubject}&body=${encodedMessage}`,
      urlToShare,
      product,
      analytics,
    },
    download: {
      name: 'Download',
      download: true,
      url: '',
      urlToShare,
      downloadUrl,
      analytics,
    },
    copyLink: {
      name: 'Copy',
      url: '',
      urlToShare,
      product,
      analytics,
    },
  }
}

const buildUrl = async (appConfig) => {
  const { url, urlToShare, name, product } = appConfig
  if (name === 'FamilySearch Chat') {
    return url
  }
  let newUrl = urlToShare
  if (typeof urlToShare === 'function') {
    const returnedUrl = await urlToShare()
    newUrl = typeof returnedUrl === 'string' ? returnedUrl : ''
  }
  const shareableUrl = addAnalyticsParam(newUrl, product, name)

  if (name === 'Copy') {
    return shareableUrl // The Copy url should not be encoded.
  }

  return url + encodeURIComponent(shareableUrl)
}

/**
 * Safari is blocking any call to window.open() which is made inside an async
 * call. So as a work around, we open the window, then make the function call
 * to get the url (however long that takes) and then redirect the window.
 * For Reference:
 * https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
 * @param {Object} appConfig config for the app
 * @param {Object} specs the window specs
 */
const socialButtonClickForSafari = async (appConfig, specs) => {
  if (!isIosDevice()) appConfig.window.ref = window.open('', appConfig.name, specs)

  appConfig.formattedUrl = await buildUrl(appConfig)
  handleAnalytics(appConfig)

  if (isIosDevice()) {
    window.location = appConfig.formattedUrl
  } else {
    appConfig.window.ref.location = appConfig.formattedUrl
  }
}

const socialButtonClick = async ({ appConfig, ...socialOverlay }) => {
  const specs = buildWindowSpecs(appConfig)
  if ((isUsingBrowser('Safari') && !isUsingBrowser('Chrome')) || isUsingBrowser('Firefox')) {
    socialButtonClickForSafari(appConfig, specs)
  } else {
    appConfig.formattedUrl = await buildUrl(appConfig)
    handleAnalytics(appConfig)
    openWindow(appConfig, specs)
  }
  if (socialOverlay && Object.keys(socialOverlay).length > 0) socialOverlay.close()
}

// Email needs the URL synchronously to set the `to` prop for its Link
const getEmailLinkUrl = (appConfig) => {
  const { url, urlToShare, name, product } = appConfig
  const shareableUrl = addAnalyticsParam(urlToShare, product, name)
  return url + encodeURIComponent(shareableUrl)
}

const kakaoTalkShare = ({ title, description, imageUrl, urlToShare }) => {
  window.Kakao.Link.sendDefault({
    objectType: 'feed',
    content: {
      title,
      description,
      imageUrl,
      link: {
        mobileWebUrl: urlToShare,
        webUrl: urlToShare,
      },
    },
  })
}

// KakaoTalk requires an SDK. As an optimization, the SDK will only be loaded when the KakaoTalk share button has been selected
const kakaoTalkClicked = (config, { setLoadingButton, index: buttonIndex }) => {
  if (!hasWindow) return

  const kakaoSDK = document.getElementById('kakaoSDK')

  if (!kakaoSDK) {
    setLoadingButton({ loading: true, index: buttonIndex })
    const script = document.createElement('script')
    script.id = 'kakaoSDK'
    script.src = 'https://developers.kakao.com/sdk/js/kakao.min.js'
    script.onload = function onKakaoSDKLoad() {
      window.Kakao.init(kakaoSDKId)
      kakaoTalkShare(config)
      setLoadingButton({ loading: false, index: null })
    }
    document.body.appendChild(script)
  } else {
    kakaoTalkShare(config)
  }
}

const getSupportedOptions = ({ config, t, copyOverlay, onEmailClick, onDownloadClick, ...socialOverlay }) => {
  return {
    familysearchChat: {
      Icon: SocialChat,
      label: t('social.familysearchChat.label', 'FamilySearch Chat'),
      onClick: () => socialButtonClick({ appConfig: config.familysearchChat, ...socialOverlay }),
      needsAddPersonalDataPermission: true,
      needsLegalAgePermission: true,
    },
    facebook: {
      Icon: LogoFacebook,
      label: t('social.facebook.label', 'Facebook'),
      onClick: () => socialButtonClick({ appConfig: config.facebook, ...socialOverlay }),
      needsAddPersonalDataPermission: true,
      needsLegalAgePermission: false,
    },
    messenger: {
      Icon: LogoFbMessenger,
      label: t('social.facebook-messenger.label', 'Messenger'),
      onClick: () => socialButtonClick({ appConfig: config.messenger, ...socialOverlay }),
      needsAddPersonalDataPermission: true,
      needsLegalAgePermission: false,
    },
    twitter: {
      Icon: LogoTwitter,
      label: t('social.x.label', 'X'),
      onClick: () => socialButtonClick({ appConfig: config.twitter, ...socialOverlay }),
      needsAddPersonalDataPermission: true,
      needsLegalAgePermission: false,
    },
    whatsApp: {
      Icon: LogoWhatsApp,
      label: t('social.whatsapp.label', 'WhatsApp'),
      onClick: () => socialButtonClick({ appConfig: config.whatsApp, ...socialOverlay }),
      needsAddPersonalDataPermission: false,
      needsLegalAgePermission: false,
    },
    line: {
      Icon: LogoLine,
      label: t('social.line.label', 'Line'),
      onClick: () => socialButtonClick({ appConfig: config.line, ...socialOverlay }),
      needsAddPersonalDataPermission: true,
      needsLegalAgePermission: false,
    },
    kakaoTalk: {
      Icon: LogoKakaoTalk,
      label: t('social.kakao-talk.label', 'Kakao Talk'),
      onClick: (loadingButtonConfig) => {
        handleAnalytics(config.kakaoTalk)
        kakaoTalkClicked(config.kakaoTalk, loadingButtonConfig)
      },
      needsAddPersonalDataPermission: true,
      needsLegalAgePermission: false,
    },
    kakaoStory: {
      Icon: LogoKakaoStory,
      label: t('social.kakao-story.label', 'Kakao Story'),
      onClick: () => socialButtonClick({ appConfig: config.kakaoStory, ...socialOverlay }),
      needsAddPersonalDataPermission: true,
      needsLegalAgePermission: false,
    },
    email: {
      Icon: SocialEmail,
      label: t('common-ui:email.address.abbr', 'Email'),
      external: true,
      target: '_blank',
      rel: 'noopener noreferrer',
      // onEmailClick allows the consumer to display their own email form
      // If onEmailClick is not present, `to` will open the device's default email service
      to: !onEmailClick && getEmailLinkUrl(config.email),
      onClick: () => {
        handleAnalytics(config.email)
        onEmailClick?.()
        socialOverlay?.close?.()
      },
      needsAddPersonalDataPermission: false,
      needsLegalAgePermission: false,
    },
    download: {
      Icon: ContentDownload,
      label: t('social.download.action', 'Download'),
      download: true,
      to: config.download.downloadUrl ? config.download.downloadUrl : undefined,
      onClick: () => (onDownloadClick ? onDownloadClick() : handleAnalytics(config.download)),
      needsAddPersonalDataPermission: false,
      needsLegalAgePermission: false,
    },
    copyLink: {
      Icon: ContentLink,
      label: t('social.copy-link.action', 'Copy Link'),
      onClick: (...args) => {
        handleAnalytics(config.copyLink)
        copyOverlay.handleClick(...args)
      },
      needsAddPersonalDataPermission: false,
      needsLegalAgePermission: false,
    },
  }
}

const CopyDialog = ({ copyOverlay, subject, copyLinkContent, onCopyClick, t, config }) => {
  const [copyUrl, setCopyUrl] = useState()
  const { isOpen, transitioning } = copyOverlay
  const targetRef = useRef()

  useEffect(() => {
    let isCurrent = true
    if (isOpen && transitioning) {
      ;(async () => {
        try {
          const formattedUrl = await buildUrl(config)
          if (isCurrent) setCopyUrl(formattedUrl)
        } catch (err) {
          console.error(err)
        }
      })()
    }
    return () => {
      isCurrent = false
    }
  }, [config, isOpen, transitioning])

  return copyUrl ? (
    <DialogOverlay {...copyOverlay} title={subject} focusRef={targetRef}>
      {copyLinkContent && (
        <>
          <Paragraph centered>{copyLinkContent}</Paragraph>
          <Separator size="xs" />
        </>
      )}
      <Suspense fallback={<Skeleton.Image width={300} height={300} />}>
        <Row alignX="center">
          <QRCodeSVG
            value={copyUrl}
            role="img"
            aria-label={t('social.share.qrCodeLabel', 'QR code of the link to this page')}
            className={qrCodeCss}
          />
        </Row>
      </Suspense>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <TextField type="url" value={copyUrl} placeholder="http://..." growX />
          <Button ref={targetRef} onClick={() => onCopyClick(copyUrl)} emphasis="medium">
            {t('common-ui:copy.action', 'Copy')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  ) : null
}

const SocialListItems = ({ platforms, copyOverlay, onEmailClick, onDownloadClick, config, ...props }) => {
  const [loadingButton, setLoadingButton] = useState({ index: null, loading: false })
  const { t } = useTranslation()
  const [canAddPersonalData, canAddPersonalDataStatus] = useCanAddPersonalData()
  const [hasLegalAgePermission, legalAgePermissionStatus] = usePermission('FsChatLegalAgePermission', 'FsChat')
  const supportedOptions = getSupportedOptions({
    config,
    t,
    copyOverlay,
    onEmailClick,
    onDownloadClick,
    ...props,
  })
  const availableOptions = Object.keys(supportedOptions).reduce((accumulator, currentValue, index) => {
    const option = { ...supportedOptions[currentValue] }
    const buttonIcon = loadingButton.loading && loadingButton.index === index ? NoticeLoading : option.Icon
    option.startElement = <ListItem.Icon Icon={buttonIcon} />
    option.primaryText = <Ellipsis>{option.label}</Ellipsis>
    option.key = option.label
    const onClickWithConfig = option.onClick.bind(option)
    option.onClick = () => onClickWithConfig({ setLoadingButton, index })
    delete option.Icon
    delete option.label
    accumulator[currentValue] = option
    return accumulator
  }, {})
  let items = platforms.map((option) => availableOptions[option])

  // Filter out Social Media Entries that are blocked by CanAddData and LegalAge Permissions if not allowed to access
  // Filter out permission props so they don't get passed to DOM element
  if (canAddPersonalDataStatus !== 'loading' && !canAddPersonalData) {
    items = items.filter((item) => !item.needsAddPersonalDataPermission)
  }

  if (legalAgePermissionStatus !== 'loading' && !hasLegalAgePermission) {
    items = items.filter((item) => !item.needsLegalAgePermission)
  }

  items = items.map((item) => {
    const { needsAddPersonalDataPermission, needsLegalAgePermission, ...rest } = item
    return rest
  })

  return items.map((item) => item && <ListItem {...item} />)
}

export function SocialMenu({
  useIconButton = false,
  buttonEmphasis = 'medium',
  billboardButtonColor,
  platforms = defaultPlatforms,
  sharable,
  copyLinkContent,
  product = 'fs',
  analytics = {},
  inline = true,
  buttonText,
  hideIcon = false,
  Icon,
  onEmailClick,
  onDownloadClick,
  downloadUrl,

  ...props
}) {
  const { t } = useTranslation()
  const menuButtonRef = useRef()
  const menuOverlay = useMenuOverlay()
  const copyOverlay = useOverlay({ focusBackRef: menuButtonRef })
  const showStatusOverlay = useStatusOverlay()

  // If url is a function, store its return value when the menu is first opened
  const { url: urlStringOrFunc } = sharable
  const isUrlFunction = typeof urlStringOrFunc === 'function'
  const [url, setUrl] = useState(isUrlFunction ? null : urlStringOrFunc)
  const [loadingUrl, setLoadingUrl] = useState(false)

  useEffect(() => {
    if (!isUrlFunction) setUrl(sharable.url)
  }, [isUrlFunction, sharable.url])

  product = product === '' ? 'fs' : product
  platforms = platforms.length === 0 ? defaultPlatforms : platforms
  sharable.subject = sharable.subject || ''

  // Don’t generate config until we have the URL
  const config = url ? configs({ ...sharable, url }, product, analytics, downloadUrl) : null
  const shareText = buttonText || t('social.share.action', 'Share')
  const ButtonIcon = loadingUrl ? NoticeLoading : Icon || SocialShare

  const handleMenuButtonClick = async (event) => {
    const { currentTarget: button } = event
    if (url) {
      menuOverlay.open({ target: button })
    } else {
      setLoadingUrl(true)
      try {
        const returnedUrl = await urlStringOrFunc()
        if (returnedUrl && typeof returnedUrl === 'string') {
          setUrl(returnedUrl)
          menuOverlay.open({ target: button })
        }
      } catch (error) {
        // Consumer is responsible for handling errors (e.g., with a StatusOverlay)
      } finally {
        setLoadingUrl(false)
      }
    }
  }

  const ButtonComponent = billboardButtonColor ? BillboardButton : Button
  const menuButton = useIconButton ? (
    <IconButton
      {...sanitizeProps(props)}
      {...menuOverlay.menuButtonProps}
      ref={menuButtonRef}
      Icon={ButtonIcon}
      aria-label={shareText}
      onClick={handleMenuButtonClick}
      disabled={loadingUrl}
    />
  ) : (
    <ButtonComponent
      {...sanitizeProps(props)}
      {...menuOverlay.menuButtonProps}
      ref={menuButtonRef}
      Icon={hideIcon ? null : ButtonIcon}
      emphasis={buttonEmphasis}
      color={billboardButtonColor}
      onClick={handleMenuButtonClick}
      disabled={loadingUrl}
    >
      {shareText}
    </ButtonComponent>
  )

  const handleCopy = useCallback(
    (text) => {
      copyToClipboard(text)
      showStatusOverlay({
        message: t('social.copy-confirmation', 'Link copied to clipboard'),
        transient: true,
      })
      copyOverlay.close()
    },
    [copyOverlay, showStatusOverlay, t]
  )

  return (
    <>
      {menuButton}
      <MenuOverlay {...menuOverlay} inline={inline} aria-label={t('social.share-menu', 'Share Menu')}>
        {url && (
          <SocialListItems
            config={config}
            platforms={platforms}
            copyOverlay={copyOverlay}
            onEmailClick={onEmailClick}
            onDownloadClick={onDownloadClick}
          />
        )}
      </MenuOverlay>
      {url && (
        <CopyDialog
          copyOverlay={copyOverlay}
          subject={sharable.subject}
          copyLinkContent={copyLinkContent}
          onCopyClick={(copyUrl) => handleCopy(copyUrl)}
          t={t}
          config={config.copyLink}
        />
      )}
    </>
  )
}

const commonPropTypes = {
  /** The product that needs to log cid analytic information about what features are being used. e.g. 'mem' for memories */
  product: PropTypes.string,

  /** The analytics metrics to send to Adobe Analytics for clicks on buttons to open a share dialog overlay. Analytics metrics are only sent if this property is specified.  */
  analytics: PropTypes.shape({
    /** The name of the link or button that was clicked to launch the menu */
    link_name: PropTypes.string,

    /** The name of the page where the SocialDialog is displayed. */
    link_page: PropTypes.string,

    /** The name of the web app that served the page. If not specified, the product prop value will be used. */
    link_app: PropTypes.string,
  }),

  /** The payload to share with the chosen option. `url` being a function is supported _only_ by SocialMenu (and not by SocialBar or SocialDialog).  */
  sharable: PropTypes.shape({
    /** Should be a fully qualified https://www.familysearch.org... url in order to work and support analytics. */
    url: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,

    /** The text to display in place of the full url. Only supported for FamilySearch Chat. If not provided, the whole url will be displayed. */
    urlDisplayText: PropTypes.string,

    /** The payload to share with the chosen option. Not all social platforms support a subject. */
    subject: PropTypes.string,

    /** The message to share. Not all social platforms support a message. */
    message: PropTypes.string,

    /** The image url to share. Currently only supported/necessary for Kakao Talk */
    imageUrl: PropTypes.string,
  }).isRequired,

  /** Displays in the copy dialog content area */
  copyLinkContent: PropTypes.string,

  /** Shows the chosen share options. */
  platforms: PropTypes.arrayOf(PropTypes.oneOf([...defaultPlatforms, 'kakaoStory', 'kakaoTalk', 'download'])),

  /** Fired when email is an option specified in `platforms` and clicked. When not specified, the default email client opens. */
  onEmailClick: PropTypes.func,

  /** Fired when download is an option specified in `platforms` and clicked. When not specified, the `sharable`'s `downloadUrl` is used. */
  onDownloadClick: PropTypes.func,

  /** Download URL. Should still be fully qualified. Represents an image or document, not a webpage. For more dynamic content, use the onDownloadClick callback. */
  downloadUrl: PropTypes.string,
}

SocialMenu.propTypes = {
  ...commonPropTypes,

  /** The emphasis to give the Share button. `lightHigh` is only valid when `billboardButtonColor` is provided. */
  buttonEmphasis: PropTypes.oneOf(['low', 'medium', 'high', 'lightHigh']),

  /** Text to be displayed in the button. Defaults to "Share" */
  buttonText: PropTypes.string,

  /** A billboard color for the button. NOTE: Including this prop will switch the underlying Button to BillboardButton. */
  billboardButtonColor: PropTypes.oneOf(['blue', 'green', 'yellow', 'red', 'purple', 'gray']),

  /** An Icon to display in the SocialMenu button. Defaults to SocialShare. */
  Icon: PropTypes.elementType,

  /** Optionally have the menu be next to the button in the DOM for better accessibility.  If set to false, this overlay will be rendered in a portal instead. Use this prop if your overlay is being cut off because one of its ancestors has `overflow: hidden` */
  inline: PropTypes.bool,

  /** When set, the Share button becomes an IconButton */
  useIconButton: PropTypes.bool,

  /** Hide the share icon for the button with text. Defaults to false */
  hideIcon: PropTypes.bool,
}

function SocialButton({ label, useIconButtons, ...props }) {
  if (useIconButtons) {
    return <IconButton title={label} {...props} />
  }
  return <Button {...props}>{label}</Button>
}

export function SocialBar({
  useIconButtons = false,
  platforms = defaultPlatforms,
  sharable,
  copyLinkContent,
  product = 'fs',
  analytics = {},
  onEmailClick,
  onDownloadClick,
  downloadUrl,
  color = 'blue',
  /** INTERNAL */
  onCopyClick,
  ...props
}) {
  const [loadingButton, setLoadingButton] = useState({ index: null, loading: false })
  const { t } = useTranslation()
  const copyOverlay = useOverlay()
  const showStatusOverlay = useStatusOverlay()
  const [canAddPersonalData, canAddPersonalDataStatus] = useCanAddPersonalData()
  const [hasLegalAgePermission, legalAgePermissionStatus] = usePermission('FsChatLegalAgePermission', 'FsChat')

  product = product === '' ? 'fs' : product
  platforms = platforms.length === 0 ? defaultPlatforms : platforms
  sharable.subject = sharable.subject || ''
  const { subject } = sharable
  const config = configs(sharable, product, analytics, downloadUrl)
  const supportedOptions = getSupportedOptions({
    config,
    t,
    copyOverlay,
    onEmailClick,
    onDownloadClick,
  })
  let buttons = platforms.map((platform) => ({ ...supportedOptions[platform], platform }))

  // Filter out Social Media Entries that are blocked by CanAddData Permissions if not allowed to access
  // Filter out needsAddPersonalDataPermission prop so it doesn't get passed to DOM element
  if (canAddPersonalDataStatus !== 'loading' && !canAddPersonalData) {
    buttons = buttons.filter((button) => !button.needsAddPersonalDataPermission)
  }

  if (legalAgePermissionStatus !== 'loading' && !hasLegalAgePermission) {
    buttons = buttons.filter((button) => !button.needsLegalAgePermission)
  }

  buttons = buttons.map((item) => {
    const { needsAddPersonalDataPermission, needsLegalAgePermission, ...rest } = item
    return rest
  })

  const handleCopy = useCallback(
    (text) => {
      copyToClipboard(text)
      showStatusOverlay({
        message: t('social.copy-confirmation', 'Link copied to clipboard'),
        transient: true,
      })
      copyOverlay.close()
      onCopyClick()
    },
    [copyOverlay, showStatusOverlay, t, onCopyClick]
  )
  const buttonIcon = (index, Icon) => (loadingButton.loading && loadingButton.index === index ? NoticeLoading : Icon)

  return (
    <>
      <Row wrap {...props}>
        {buttons.map(({ label, Icon, onClick, platform, ...buttonProps }, index) => {
          // For the copy button, we need to pass the event to useOverlay's handleClick
          // to ensure focus is returned to the button when the dialog closes.
          const finalOnClick =
            platform === 'copyLink'
              ? (event) => {
                  handleAnalytics(config.copyLink)
                  copyOverlay.handleClick(event)
                }
              : () => onClick({ setLoadingButton, index })

          return (
            <SocialButton
              key={label}
              Icon={buttonIcon(index, Icon)}
              onClick={finalOnClick}
              label={label}
              useIconButtons={useIconButtons}
              color={color}
              {...buttonProps}
            />
          )
        })}
      </Row>

      <CopyDialog
        copyOverlay={copyOverlay}
        subject={subject}
        copyLinkContent={copyLinkContent}
        onCopyClick={(copyUrl) => handleCopy(copyUrl)}
        t={t}
        config={config.copyLink}
      />
    </>
  )
}

SocialBar.propTypes = {
  ...commonPropTypes,

  /** When set, the bar of Buttons become IconButtons */
  useIconButtons: PropTypes.bool,

  /** Button color */
  color: PropTypes.oneOf(['blue', 'gray']),
}

export function SocialDialog({
  platforms = defaultPlatforms,
  sharable,
  copyLinkContent,
  product = 'fs',
  analytics = {},
  onEmailClick,
  onDownloadClick,
  close,
  ...props
}) {
  const { t } = useTranslation()

  const onCopyClick = useCallback(() => {
    close()
  }, [close])
  const content = (
    <LayoutBand>
      <SocialBar
        sharable={sharable}
        product={product}
        analytics={analytics}
        platforms={platforms}
        onDownloadClick={onDownloadClick}
        onEmailClick={onEmailClick}
        onCopyClick={onCopyClick}
        alignX="center"
        {...props}
      />
    </LayoutBand>
  )
  return (
    <DialogOverlay {...props} close={close} title={t('social.share.action', 'Share')}>
      {content}
    </DialogOverlay>
  )
}

SocialDialog.propTypes = {
  ...commonPropTypes,

  /** REQUIRED: Social Dialog needs all the props provided from the useOverlay hook in order to work. */
  '{...useOverlay()}': PropTypes.object,
}
